import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, Download, Eye, Sparkles, Loader2, AlertCircle, CheckCircle2, Package } from "lucide-react";
import { toast } from "sonner";

/** Pick the most-recently-created doc matching `templateKey`. Hardening
 *  against a backend that appends (rather than replaces) on each generate. */
function pickLatest(documents, templateKey) {
  return (documents || [])
    .filter((d) => d.template_key === templateKey)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
}

/**
 * Will-specific document action panel.
 *
 * Surfaces quick Generate / Preview / Download controls for the two
 * Will-discipline templates (`will` and `annexure_a`) directly on the
 * admin Matter Detail page so lawyers don't have to dig through the
 * generic structured-doc dropdown.
 *
 *   props.matter        canonical matter object
 *   props.documents     existing documents on the matter
 *   props.onChanged()   callback after generate succeeds
 *
 * Behaviour:
 *   • If a `will` document already exists → show Preview + Download.
 *   • If not → show Generate button (calls 422-friendly endpoint).
 *   • Annexure A row is rendered only when the testamentary trust is
 *     declared on `matter.entities.will` (creates_testamentary_trust or
 *     trust_name) — otherwise hidden entirely.
 *   • If 422 missing-fields response, the inline panel surfaces the
 *     "Complete required fields to generate Will" guidance and the list
 *     of missing fields.
 */
export default function WillDocumentActions({ matter, documents, onChanged }) {
  if ((matter?.matter_type || matter?.service_type) !== "will") return null;

  const will = matter?.entities?.will || {};
  const hasTrust = Boolean(will.creates_testamentary_trust || will.trust_name);

  const willDoc     = pickLatest(documents, "will");
  const annexureDoc = pickLatest(documents, "annexure_a");

  const downloadBundle = () => {
    api.get(`/admin/matters/${matter.id}/will-bundle.zip`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/zip" });
        const a    = document.createElement("a");
        const ref  = (matter.id || "").slice(0, 8).toUpperCase();
        const last = (matter.client_name || "Client").split(" ").pop().replace(/[^A-Za-z0-9]/g, "");
        a.href = URL.createObjectURL(blob);
        a.download = `EH_Inc_Will_Bundle_${last}_${ref}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        toast.success("Will bundle downloaded");
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div className="bg-white border border-zinc-200 mb-5" data-testid="will-doc-actions">
      <div className="px-5 py-3 bg-zinc-900 text-white flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-300">EH Inc · Wills Workspace</div>
            <div className="text-sm font-medium">Last Will and Testament{hasTrust ? " + Annexure A" : ""}</div>
          </div>
        </div>
        {willDoc && (
          <button
            type="button"
            onClick={downloadBundle}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-medium bg-white text-zinc-900 hover:bg-zinc-100"
            data-testid="download-will-bundle-btn"
          >
            <Package className="h-3.5 w-3.5" />
            Download Will Bundle{hasTrust && annexureDoc ? " (ZIP)" : ""}
          </button>
        )}
      </div>

      <ul className="divide-y divide-zinc-200">
        <WillRow
          label="Last Will and Testament"
          subtitle="Final will document — 7-clause EH Inc template."
          matter={matter}
          doc={willDoc}
          templateKey="will"
          onChanged={onChanged}
        />
        {hasTrust && (
          <WillRow
            label="Annexure A — Testamentary Trust Deed"
            subtitle={will.trust_name
              ? `Trust: ${will.trust_name} · 24-clause Special Type A`
              : "24-clause Special Type A testamentary trust deed."}
            matter={matter}
            doc={annexureDoc}
            templateKey="annexure_a"
            onChanged={onChanged}
          />
        )}
      </ul>

      {!hasTrust && (
        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 text-xs text-zinc-500 flex items-start gap-2"
             data-testid="will-no-trust-note">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>
            No testamentary trust declared. Annexure A is hidden — toggle
            <code className="px-1 mx-1 bg-white border border-zinc-300">creates_testamentary_trust</code>
            (or set <code className="px-1 mx-1 bg-white border border-zinc-300">trust_name</code>) in the entities to enable it.
          </span>
        </div>
      )}
    </div>
  );
}


function WillRow({ label, subtitle, matter, doc, templateKey, onChanged }) {
  const [busy, setBusy]       = useState(false);
  const [missing, setMissing] = useState(null);   // string[] | null

  const matterId = matter.id;

  const generate = async () => {
    setBusy(true);
    setMissing(null);
    try {
      const { data } = await api.post(
        `/admin/matters/${matterId}/documents/generate-structured`,
        { template_key: templateKey },
      );
      toast.success(`${label} generated`, { description: data?.audit_event?.summary || "Draft ready for review." });
      onChanged?.();
    } catch (e) {
      const status = e?.response?.status;
      const body   = e?.response?.data;
      if (status === 422 && body?.blocked) {
        setMissing(body.missing || []);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const previewPdf = () => {
    if (!doc) return;
    // Open the PDF in a new tab — uses the same authenticated axios client
    // so we fetch as a blob and open via object URL.
    api.get(`/admin/documents/${doc.id}/pdf`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url  = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        // Revoke after a delay — the new tab needs the URL alive.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const downloadPdf = () => {
    if (!doc) return;
    api.get(`/admin/documents/${doc.id}/pdf`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${(doc.template_name || label).replace(/\s+/g, "_")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        toast.success("Download started");
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <li className="p-5" data-testid={`will-row-${templateKey}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="font-medium text-sm text-zinc-900">{label}</div>
          <div className="text-xs text-zinc-500 mt-1">{subtitle}</div>
          {doc && (
            <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> Draft generated
              </span>
              <span className="text-zinc-400">·</span>
              <span>{new Date(doc.created_at).toLocaleString()}</span>
              {doc.last_edited_at && (
                <>
                  <span className="text-zinc-400">·</span>
                  <span>Edited {new Date(doc.last_edited_at).toLocaleString()}</span>
                </>
              )}
              <span className="text-zinc-400">·</span>
              <span className="uppercase tracking-wider text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5">
                {(doc.status || "").replace(/_/g, " ")}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {doc ? (
            <>
              <Button
                variant="outline" size="sm" className="rounded-none"
                onClick={previewPdf}
                data-testid={`preview-${templateKey}-btn`}
              >
                <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview {templateKey === "annexure_a" ? "Annexure A" : "Will"}
              </Button>
              <Button
                size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-800"
                onClick={downloadPdf}
                data-testid={`download-${templateKey}-btn`}
              >
                <Download className="h-3.5 w-3.5 mr-1.5" /> Download {templateKey === "annexure_a" ? "Annexure A" : "Will"}
              </Button>
              <Link
                to={`/admin/matters/${matterId}/documents/${doc.id}`}
                className="text-xs text-zinc-500 hover:text-zinc-900 px-2"
                data-testid={`open-${templateKey}-workspace`}
              >
                Open workspace →
              </Link>
            </>
          ) : (
            <Button
              size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-800"
              onClick={generate} disabled={busy}
              data-testid={`generate-${templateKey}-btn`}
            >
              {busy
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Generating…</>
                : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate {templateKey === "annexure_a" ? "Annexure A" : "Will"}</>}
            </Button>
          )}
        </div>
      </div>

      {missing && missing.length > 0 && (
        <div className="mt-3 border border-amber-300 bg-amber-50 p-3" data-testid={`will-missing-${templateKey}`}>
          <div className="text-xs font-medium text-amber-900 flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5" />
            Complete required fields to generate {templateKey === "annexure_a" ? "Annexure A" : "Will"}
          </div>
          <ul className="text-[12px] text-amber-800 list-disc pl-5 mt-1.5 space-y-0.5">
            {missing.map((f) => <li key={f}>{f}</li>)}
          </ul>
          {matter.portal_token && (
            <button
              onClick={() => {
                const url = `${window.location.origin}/client/${matter.portal_token}`;
                navigator.clipboard.writeText(url);
                toast.success("Portal link copied");
              }}
              className="mt-2 text-[11px] uppercase tracking-wider font-medium text-zinc-700 hover:text-zinc-900 underline"
            >
              Copy portal link for client
            </button>
          )}
        </div>
      )}
    </li>
  );
}
